/* eslint-disable no-console */
import GameAccessRestrictedDialog from 'components/dialogs/game-access-restricted';
import AccountLockedModal from 'components/dialogs/game-access-restricted/account-locked';
import { getSnapshot } from 'mobx-state-tree';
import nxLog from 'service/nx-log';
import { IProductLibrary } from 'store/apps/product-library';
import { rootStore } from 'store/root';
import { ProductState } from 'types/enum';
import { CoreApi, IASapi } from './axios-api';
import { ApiError, CoreError } from './error';
import plex from './plex';
import { serverOptions } from './server-options';
import { callApiWithIdToken } from './session-api';
import { sleep } from './utils';

const { win, application, coreappIpc, nxlApi, electron, _pathParse, _pathJoin } = window;

const appApi = {
    lastActiveTime: {} as any,
    lastActiveTimeAny: 0,
    autoUpdaterStarted: false,
    appClosed: {} as any,
    canceled: {} as any,
    toySdkProducts: ['59789', '59801', '59802', '59803', '59804'], // 메이플M: 59789, toy 샘플: 59801, 59802, 59803, 59804
    // use 30020 ~ 30040 error code for FE exception
    async launchApp(productInfo: IProductLibrary) {
        const {
            productId,
            isSdsPlayable,
            installInfo: { currentState },
        } = productInfo;

        plex.sendStats('game_launch', 'attempt', productId);
        const productConfig = await coreappIpc.send('getProductConfig', productId);

        if (!productInfo.detail) {
            await productInfo.fetchDetail();
        }
        if (!productInfo.detail) {
            throw new CoreError(30025, 'cannot fetch game detail info');
        }
        // SSI exclusive logic
        if (!isSdsPlayable) {
            if (currentState & ProductState.BUSY) {
                throw new CoreError(20511, 'game is busy');
            }
            // verifying
            const manifestHashUrl = await this.getCurrentManifestHashUrl(productId, productConfig);
            const [result] = await coreappIpc.send('verifyApp', productInfo.detail.coreFormat, manifestHashUrl, false, false, null);
            if (result === false) {
                nxlApi.appsInstaller.pauseAll();
                plex.sendStats('game_launch', 'cancel', productId, { reason: 'game needs to be updated' });
                this.installApp(productInfo);
                return false;
            }
        }

        const productDetail = getSnapshot(productInfo.detail);
        if (!productDetail) return;

        let playableData;
        await productInfo.fetchAccess();

        try {
            if (productInfo.access?.isMinor) {
                const data = await GameAccessRestrictedDialog({ product: productInfo });
                if (!Array.isArray(data)) return; // normal dialog close
                const [verified, result] = data;
                if (!verified) {
                    if (result) throw result;
                    return;
                }
                playableData = result;
            } else {
                // returns ip country
                const { data } = await CoreApi.post('/game-auth2/v1/playable', { productId });
                playableData = data;
            }
        } catch (e) {
            const errorCode = (e as ApiError).code;
            nxLog.send(
                {
                    comment: 'check playable',
                    errorcode: 1, // error
                    stage: 904,
                    productId,
                },
                {
                    arena_error_code: errorCode,
                    arena_error_message: (e as ApiError).message,
                }
            );
            switch (errorCode) {
                case 20234:
                case 30417:
                    // minor account is inactivated
                    AccountLockedModal({ parentEmail: productInfo?.parentEmail });
                    window.dataLayer.push({
                        event: `Parent Verification Game Access Blocked`,
                        gameName: productInfo.displayName,
                    });
                    return;
            }
            throw e;
        }

        nxLog.send({
            comment: 'check playable',
            stage: 904,
            productId,
        });

        await coreappIpc.send('runInstallScript', productId, productDetail.requiredInstallationSoftware);

        // Install SDK
        if (!(await coreappIpc.send('installSDK'))) {
            throw new CoreError(20512, 'failed to update SDK');
        }

        let { parameters, executablePath } = productInfo;
        const { workingDirectory } = productDetail;

        if (!productConfig.currentBranch || productConfig.currentBranch === 'Public') {
            productConfig.currentBranch = 'public';
            await coreappIpc.send('setCurrentBranch', productId, 'public');
        }

        if (productInfo.maintenance.useToy) {
            await productInfo.maintenance.fetchToy();
            if (!productInfo.maintenance.mode) {
                await productInfo.maintenance.fetch(true);
            }

            if (!productInfo.access?.isDeveloper && productInfo.maintenance.mode) {
                throw new CoreError(30408, productInfo.maintenance.message);
            }
        }

        // enable local override for launch args
        if (productConfig.customLaunchConfigEnable) {
            if (productInfo.access?.isDeveloper) {
                parameters = productConfig.customLaunchConfigArgs;
                if (productConfig.customLaunchConfigExe) {
                    executablePath = productConfig.customLaunchConfigExe;
                }
            }
        }

        let cwd = productConfig.installPath;

        let launchCmd = [];

        parameters = await this.processArgs(parameters, {
            productId,
            serviceId: productInfo.currentBranch?.serviceId || '',
            ...playableData,
        }).catch((e) => {
            throw new CoreError(e.code || 30020, JSON.stringify(e));
        });

        if (_pathParse(executablePath).root === '') {
            launchCmd = [_pathJoin(cwd, executablePath)];
        } else {
            launchCmd = [executablePath];
        }

        if (workingDirectory) {
            cwd = _pathJoin(cwd, workingDirectory);
        }

        if (parameters.length > 0) {
            launchCmd = launchCmd.concat(parameters);
        }
        // vindictus only for backword compatibility : it can be remove after Jun 2021
        if (productId === '10300' && !launchCmd.includes('+server_name')) {
            launchCmd = launchCmd.concat(['+server_name', rootStore.session.selectedServer]);
        }

        if (launchCmd.length < 1) {
            throw new CoreError(20513, 'Launch command is empty');
        }

        const shellCmd = 'open';
        const argstr = this.list2cmdline(launchCmd.slice(1, launchCmd.length));

        let manifestHash = null;
        if (!isSdsPlayable) {
            try {
                [manifestHash] = await coreappIpc.send('nrtLoadManifestFromFile', productConfig.localManifest);
            } catch (e) {
                plex.sendError('coreappIpc:nrtLoadManifestFromFile', e);
            }
        }
        console.info('Launch command: ', launchCmd[0], argstr);

        if (!isSdsPlayable) nxlApi.appsInstaller.pauseAll();

        // game start
        await coreappIpc.send('shellExecute', launchCmd[0], argstr, shellCmd, cwd);

        if (isSdsPlayable) {
            // streaming_ingame
            nxlApi.frontEnd.pub('setStreamingInGame', { productId, value: true });
        } else {
            await coreappIpc.send('setProductState', productId, ProductState.INGAME);
        }

        nxLog.send({
            comment: 'launch game client',
            stage: 905,
            productId,
        });

        plex.sendStats('game_launch', 'success', productId, {
            version: manifestHash || '',
            parameters: argstr.split(' ').join(' '),
        });
        plex.sendPartner('game_launch', productId, application.userNo);

        console.info('app launched: ', productId);
        window.dataLayer.push({ event: 'gameLaunch', gameName: productInfo.productName });

        (async () => {
            win.minimize();
            this.appClosed[productId] = false;
            this.lastActiveTime[productId] = Date.now() / 1000;

            const message = { productID: productId };
            await sleep(1000);
            nxlApi.appsLauncherEvents.appRunning(message);
            // console.log(`waiting for app to be closed: ${productId}`);
            const waitTime = 30.0;
            if (this.toySdkProducts.includes(productId)) {
                await sleep(waitTime * 1000);
                this.lastActiveTime[productId] = Date.now() / 1000;
                while (Date.now() / 1000 - this.lastActiveTime[productId] < waitTime) {
                    const running = await coreappIpc.send('isGameRunning', Number(productId));
                    this.appClosed[productId] = !running;
                    if (running) {
                        this.lastActiveTimeAny = Date.now() / 1000;
                        this.lastActiveTime[productId] = this.lastActiveTimeAny;
                    }

                    if (this.appClosed[productId] && Date.now() / 1000 - this.lastActiveTime[productId] > 3.0) {
                        break;
                    }
                    await sleep(1000);
                }
            } else {
                while (Date.now() / 1000 - this.lastActiveTime[productId] < waitTime) {
                    if (this.appClosed[productId] && Date.now() / 1000 - this.lastActiveTime[productId] > 3.0) {
                        // console.log('App was closed: ', productId);
                        break;
                    }
                    await sleep(1000);
                    // console.log('Waiting for app to be inactive: ', productId);
                }
            }
            if (isSdsPlayable) {
                nxlApi.frontEnd.pub('setStreamingInGame', { productId, value: false });
            } else {
                await coreappIpc.send('setProductState', productId, ProductState.INSTALLED);
            }

            nxLog.send({
                comment: 'game client end',
                stage: 907,
                productId,
            });
            plex.sendStats('game_end', '', productId);
            if (!isSdsPlayable) nxlApi.appsInstaller.resumeAny();

            if (!this.appClosed[productId]) {
                this.appClosed[productId] = true;
                // console.log(`App was closed due to inactivity: ${productId}`);
            }
        })();
        return true;
    },
    async autoUpdater() {
        console.info('Starting auto updater');
        const autoUpdateInterval = 60 * 60 * 1000;

        const checkAndUpdate = async (productInfo: IProductLibrary) => {
            const { productId } = productInfo;
            const productConfig = await coreappIpc.send('getProductConfig', productId);
            if (
                !productConfig ||
                !productConfig.autoUpdate ||
                !(productConfig.currentState & ProductState.INSTALLED) // ProductState.UPDATE_REQUIRED is opted out because it's caused by user's intention (install cancel)
            ) {
                return;
            }
            const updatable = await coreappIpc.send('checkAutoUpdatable', productId);
            if (!updatable) {
                return;
            }

            const access = productInfo.access || (await productInfo.fetchAccess());
            if (!access.isPlayable) return;
            if (!productInfo.detail) await productInfo.fetchDetail();
            if (!productInfo.detail?.supportAutomaticUpdate) return;

            // console.debug('Auto checking for updates: ', productId);
            const manifestHashUrl = await this.getCurrentManifestHashUrl(productId, productConfig);
            const [verified] = await coreappIpc.send('verifyApp', productInfo.detail.coreFormat, manifestHashUrl, true, false, null);
            // console.debug('Auto update check result: ', verified);
            if (!verified) {
                await coreappIpc.send('installApp', productId, productInfo.detail.coreFormat, manifestHashUrl, true, false);
            }
        };

        while (true) {
            await sleep(autoUpdateInterval);
            try {
                // console.debug('Running auto updater cycle');
                // check any game has been active in the last 30 seconds:
                if (Date.now() / 1000 - this.lastActiveTimeAny < 60) {
                    // console.debug('Skipping update check since a product is in-game');
                    continue;
                }
                if (await coreappIpc.send('checkDownloadInProgress')) {
                    // console.debug('Skipping update check since a product is updating');
                    continue;
                }
                // eslint-disable-next-line guard-for-in
                for (const productInfo of rootStore.appStore.library) {
                    if (await coreappIpc.send('checkDownloadInProgress')) {
                        break;
                    }
                    try {
                        await checkAndUpdate(productInfo);
                    } catch (e) {
                        plex.sendError('autoUpdate', e, { productId: productInfo.productId });
                        continue;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        }
    },

    installAppWidthId(productId: string, options: any = {}) {
        const productInfo = rootStore.appStore.library.find((x) => x.productId === productId);
        if (productInfo) return this.installApp(productInfo, options);
        return null;
    },
    // use 30000 ~ 30019 error code for FE exception
    async installApp(productInfo: IProductLibrary, options: any = {}) {
        const { productId } = productInfo;
        if (!productId || !productInfo.detail) {
            console.error(`installApp failed - wrong productId ${productId} or cannot fetch game detail`);
            return;
        }

        const productDetail = getSnapshot(productInfo.detail);
        if (!productDetail) return;
        const productConfig = await coreappIpc.send('getOrCreateProductConfig', productId, productInfo.detail.directoryName);

        // temporary solution - it can be removed around 02/2022
        if (productConfig.installRootPath && productConfig.installRootPath.indexOf('KartRider:') > -1) {
            await coreappIpc.send(
                'setInstallRootPath',
                productId,
                productInfo.detail.directoryName,
                productConfig.installRootPath.replace('KartRider:', 'KartRider')
            );
        }
        ///

        if (productConfig.currentState & ProductState.BUSY && !options.isAutoUpdate) {
            nxlApi.appsInstallerEvents.appInstallFailed({ productID: productId, errorCode: 20420 });
            return;
        }

        const isInstall = productConfig.currentState === ProductState.NOT_INSTALLED || productConfig.installTime === 0;

        // install (not update)
        if (isInstall) {
            if (!options.skipCompatibility && !options.isAutoUpdate && electron.osVer) {
                const supportedOs = productInfo.detail.supportedOs.toStringArray;
                const supportedArch = productInfo.detail.supportedSystemType.toStringArray;
                if ((supportedOs && !supportedOs.includes(electron.osVer)) || (supportedArch && !supportedArch.includes(electron.arch))) {
                    const message = {
                        productID: productId,
                        isInstall,
                        errorCode: 20520,
                        errorData: {
                            my_os_ver: electron.osVer,
                            my_cpu_arch: electron.arch,
                            req_os_ver: supportedOs.join(', '),
                            req_cpu_arch: supportedArch.join(', '),
                        },
                    };
                    nxlApi.appsInstallerEvents.appInstallFailed(message);
                    return;
                }
            }
        }

        nxLog.send({
            comment: isInstall ? 'game install start' : 'game update start',
            stage: isInstall ? 900 : 902, // 900: install, 902: update
            productId,
        });

        const manifestHashUrl = await this.getCurrentManifestHashUrl(productId, productConfig, !options.isAutoUpdate).catch(
            (e: ApiError) => {
                nxlApi.appsInstallerEvents.appInstallFailed({
                    productID: productId,
                    isInstall,
                    errorCode: e.code || 30003,
                    errorMessage: e.message,
                });
                plex.sendStats('game_install', 'failure', productId, { reason: JSON.stringify({ ...e, message: e.message }) });
                throw e;
            }
        );

        if (isInstall) {
            const result = await coreappIpc.send('runCustomInstallScript', productId, productDetail.requiredInstallationSoftware);
            if (!result) {
                return;
            }
        }

        await coreappIpc
            .send('installApp', productId, productInfo.detail.coreFormat, manifestHashUrl, false, false, !!productInfo.streamingSupported)
            .catch((e: any) => {
                nxlApi.appsInstallerEvents.appInstallFailed({
                    productID: productId,
                    isInstall,
                    errorCode: e.code || 30004,
                    errorMessage: e.message,
                });
                coreappIpc.send('setProductState', productId, ProductState.UPDATE_REQUIRED);
                throw e;
            });

        if (options.desktopShortcut) {
            productInfo.createShortcut('desktop');
        }
        if (options.startShortcut) {
            productInfo.createShortcut('startmenu');
        }
    },
    async getCurrentManifestHashUrl(productId: string, productConfig: any, reload = false) {
        let manifestUrl;
        const productInfo: IProductLibrary | undefined = rootStore.appStore.library.find((x: any) => x.productId === productId);
        if (!productInfo) return '';
        // if (productConfig.customTargetBuildId) {
        //     await productInfo.fetchAccess();
        //     if (productInfo.access?.isDeveloper) {
        //         manifestUrl = productInfo.branches.find(x => x.branchName === 'public')?.manifestUrl || '';
        //         if (manifestUrl) {
        //             const i = manifestUrl.lastIndexOf('/');
        //             return manifestUrl.substring(0, i + 1) + productConfig.customTargetBuildId;
        //         }
        //     }
        // }
        await productInfo.fetchBranch(); // refresh
        manifestUrl = productInfo.currentBranch?.manifestUrl || '';
        if (!manifestUrl) {
            throw new CoreError(30100, "Couldn't get branch's manifestUrl");
        }
        if (manifestUrl.endsWith('.hash')) {
            const res = await fetch(manifestUrl);
            if (res.ok) {
                const hash = await res.text();
                if (hash) {
                    const i = manifestUrl.lastIndexOf('/');
                    manifestUrl = manifestUrl.substring(0, i + 1) + hash;
                } else {
                    throw new Error(`empty manifest hash: ${manifestUrl}`);
                }
            } else {
                throw new Error(`fetch manifest hash error: ${manifestUrl}, status: ${res.status}`);
            }
        }
        return manifestUrl;
    },
    /* eslint-disable no-template-curly-in-string */
    processArgs: async (args: string[], game: any) => {
        const replaceArgsFunc = async (keyword: string, asyncFuncOrValue: any) => {
            const index = args.findIndex((x: string) => x.indexOf(keyword) >= 0);
            if (index < 0) return;

            const value = typeof asyncFuncOrValue === 'function' ? await asyncFuncOrValue() : asyncFuncOrValue;
            args = args.map((x: string) => x.replace(keyword, value));
        };

        await Promise.all([
            replaceArgsFunc('${passport}', async () => {
                const {
                    data: { passport },
                } = await CoreApi.post('/passport/v2/passport', { productId: game.productId }).catch((e: ApiError) => {
                    throw new CoreError(30021, `passport api error - (code: ${e.code})`);
                });
                return passport;
            }),
            replaceArgsFunc('${country_code}', () => game.countryCode),
            replaceArgsFunc('${language_code}', () => application.params.locale.slice(0, 2).toUpperCase()),
            replaceArgsFunc(
                '${tracking_uid}',
                [10100, 10200, 10300, 50220].includes(parseInt(game.productId, 10)) ? application.userNo : application.globalUserNo // only legacy games are using membershiop user no
            ),
            replaceArgsFunc('${server_name}', () => rootStore.session.selectedServer),
            replaceArgsFunc(
                '${vs_service_id}',
                () => serverOptions.find((one) => one.name === rootStore.session.selectedServer)?.serviceId || 0
            ),
            replaceArgsFunc('${tracking_serviceid}', () => game.serviceId),
            replaceArgsFunc('${tracking_sessionid}', () => nxLog.getState(game.serviceId).sessionId),
            replaceArgsFunc('${ias_ticket}', async () => {
                const { ticket: arena_ticket } = await callApiWithIdToken('', { product_id: game.productId });
                const {
                    data: { ticket },
                } = await IASapi.post('/v1/issue/ticket/by-arena-ticket', { arena_ticket });
                return ticket;
            }),
            replaceArgsFunc('${store}', () => localStorage.getItem('store') || 'arena'),
        ]);

        return args;
    },
    list2cmdline: (seq: any) => {
        let result: any = [];
        let needquote = false;

        for (const arg of seq) {
            let bsBuf = [];

            // Add a space to separate this argument from the others
            if (result.length > 0) {
                result.push(' ');
            }

            needquote = arg.includes(' ') || arg.includes('\t') || !arg;
            if (needquote) {
                result.push('"');
            }

            for (const c of arg) {
                if (c === '\\') {
                    // Don't know if we need to double yet.
                    bsBuf.push(c);
                } else if (c === '"') {
                    // Double backslashes.
                    result.push('\\'.repeat(bsBuf.length * 2));
                    bsBuf = [];
                    result.push('\\"');
                } else {
                    // Normal char
                    if (bsBuf.length > 0) {
                        result = result.concat(bsBuf);
                        bsBuf = [];
                    }
                    result.push(c);
                }
            }

            // Add remaining backslashes, if any.
            if (bsBuf.length > 0) {
                result = result.concat(bsBuf);
            }

            if (needquote) {
                result = result.concat(bsBuf);
                result.push('"');
            }
        }

        return result.join('');
    },
};

window.appApi = appApi;

export default appApi;
